import { useMemo } from 'react'
import Modal from '@mui/material/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faPen,
  faWavePulse,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { FundingTemplateActivityEntry } from '../../api/fundingApi'
import { useStyles } from './TemplateActivityLogModal.styles'

type Props = {
  open: boolean
  onClose: () => void
  entries: FundingTemplateActivityEntry[]
  loading?: boolean
}

type Tone = 'ok' | 'bad' | 'edit'

/** Local calendar day (YYYY-MM-DD) — avoids UTC midnight skew near day boundaries. */
const localDayKey = (date: Date): string | null => {
  if (Number.isNaN(date.getTime())) return null
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const calendarDayKey = (iso: string): string | null => localDayKey(new Date(iso))

const dayGroupLabel = (iso: string, now = new Date()): string => {
  const at = calendarDayKey(iso)
  const today = localDayKey(now)
  if (!at || !today) return 'Earlier'
  const [ay, am, ad] = at.split('-').map(Number)
  const [ty, tm, td] = today.split('-').map(Number)
  const atMs = Date.UTC(ay, am - 1, ad)
  const todayMs = Date.UTC(ty, tm - 1, td)
  const diffDays = Math.round((todayMs - atMs) / (24 * 60 * 60 * 1000))
  if (diffDays <= 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  return `${diffDays} days ago`
}

const initialsFrom = (name?: string | null) => {
  const parts = (name || 'System').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'SY'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
}

const displayActor = (actor?: string | null) => {
  const raw = (actor || '').trim()
  if (!raw || raw.toLowerCase() === 'system') return 'System'
  return raw
}

const humanizeAction = (action: string) =>
  action
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase())

const toneFor = (actionType: string, summary: string): Tone => {
  const e = `${actionType} ${summary}`.toLowerCase()
  if (
    /\benabled\b|\bcreated\b|\bunarchived\b|\bcloned\b|\brestored\b|\badded\b|\buploaded\b/.test(
      e
    )
  ) {
    return 'ok'
  }
  if (
    /\bdisabled\b|\barchived\b|\bremoved\b|\bdeleted\b|\bcleared\b/.test(e)
  ) {
    return 'bad'
  }
  return 'edit'
}

/** Short category chip beside the detailed summary (fields / docs / meta). */
const categoryFor = (actionType: string): string | null => {
  const a = actionType.toLowerCase()
  if (a.includes('document_added') || a.includes('document_uploaded'))
    return 'Document uploaded'
  if (a.includes('document_removed')) return 'Document removed'
  if (a.includes('document_changed') || a.includes('document_replaced'))
    return 'Document changed'
  if (a.includes('field_cleared')) return 'Field cleared'
  if (a.includes('field')) return 'Field updated'
  if (a.includes('name')) return 'Name'
  if (a.includes('description')) return 'AI input'
  if (a.includes('calculator')) return 'Calculator'
  if (a.includes('force_aws')) return 'AWS account'
  if (a.includes('status')) return 'Status'
  if (a.includes('created')) return 'Created'
  if (a.includes('cloned')) return 'Cloned'
  if (a.includes('archived')) return 'Archived'
  return null
}

const formatRowTime = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Centered activity log modal matching AI schedule (Figma 641:7238). */
export const TemplateActivityLogModal = ({
  open,
  onClose,
  entries,
  loading,
}: Props) => {
  const { classes, cx } = useStyles()

  const groups = useMemo(() => {
    const map = new Map<string, FundingTemplateActivityEntry[]>()
    const ordered = [...entries].sort(
      (a, b) =>
        new Date(b.event_at).getTime() - new Date(a.event_at).getTime()
    )
    for (const entry of ordered) {
      const label = dayGroupLabel(entry.event_at)
      const list = map.get(label) || []
      list.push(entry)
      map.set(label, list)
    }
    return Array.from(map.entries())
  }, [entries])

  return (
    <Modal open={open} onClose={onClose} className={classes.modalOverlay}>
      <div
        className={classes.paper}
        data-testid="template-activity-panel"
        role="dialog"
        aria-labelledby="template-activity-log-title"
      >
        <div className={classes.header}>
          <div className={classes.titleRow}>
            <span className={classes.iconTile} aria-hidden>
              <FontAwesomeIcon icon={faWavePulse} />
            </span>
            <h2 id="template-activity-log-title" className={classes.title}>
              Activity log
            </h2>
          </div>
          <button
            type="button"
            className={classes.close}
            aria-label="Close activity log"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className={classes.timeline}>
          <div className={classes.timelineRail} aria-hidden />
          {loading ? (
            <p className={classes.empty}>Loading…</p>
          ) : entries.length === 0 ? (
            <p className={classes.empty}>No activity yet.</p>
          ) : (
            groups.map(([label, rows]) => (
              <div key={label} className={classes.group}>
                <div className={classes.groupHeader}>
                  <span className={classes.groupSpacer} aria-hidden />
                  <span className={classes.groupLabel}>{label}</span>
                  <span className={classes.groupRule} aria-hidden />
                </div>
                {rows.map(entry => {
                  const tone = toneFor(entry.action_type, entry.summary)
                  const actor = displayActor(entry.actor)
                  const summary = entry.summary?.trim()
                  const eventLabel =
                    summary || humanizeAction(entry.action_type)
                  const category = categoryFor(entry.action_type)
                  const showCategory =
                    Boolean(category) &&
                    category!.toLowerCase() !== eventLabel.toLowerCase()
                  return (
                    <div key={entry.id} className={classes.row}>
                      <span
                        className={cx(
                          classes.toneIcon,
                          tone === 'ok' && classes.toneOk,
                          tone === 'bad' && classes.toneBad,
                          tone === 'edit' && classes.toneEdit
                        )}
                        aria-hidden
                      >
                        <FontAwesomeIcon
                          icon={
                            tone === 'ok'
                              ? faCheck
                              : tone === 'bad'
                                ? faXmark
                                : faPen
                          }
                        />
                      </span>
                      <div className={classes.body}>
                        <div className={classes.main}>
                          <span className={classes.avatar} aria-hidden>
                            {initialsFrom(actor)}
                          </span>
                          <span className={classes.actor}>{actor}</span>
                          <span className={classes.eventLabel}>
                            {eventLabel}
                          </span>
                          {showCategory ? (
                            <span className={classes.detailPill}>
                              {category}
                            </span>
                          ) : null}
                        </div>
                        <span className={classes.time}>
                          {formatRowTime(entry.event_at)}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  )
}

export default TemplateActivityLogModal
